/**
 * ChannelLeaderboardTab — B-2026-0072
 *
 * Renders the Leaderboard tab for ChannelPage:
 *   - Ranked athlete rows: medal icons for top-3, PB score, session count, last active
 *   - Current user's own row is highlighted even if outside the visible window
 *   - Empty/disabled state renders an appropriate placeholder
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TrophyIcon,
  UserGroupIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { getChannelLeaderboard } from '@/services/leaderboardApi';
import type { LeaderboardEntry, LeaderboardResponse } from '@/services/leaderboardApi';
import type { LeaderboardRankedBy } from '@/types/goalScore';
import Spinner from '@/components/ui/Spinner';
// B-2026-0075: milestone badges on leaderboard rows
import { useUserMilestones } from '@/hooks/useMilestones';
import { MiniMilestoneRow } from '@/components/dashboard/MilestoneGrid';
// Goal Score follow-ups batch 1 (frontend): row breakdown sub-line (J §1.3)
import { leaderboardGoalBreakdownLine } from '@/utils/goalScoreCopy';

// ─── B2 (Goal Score go-live): remembered viewer preferences ────────────────
// Both the Total/Form metric choice and the sort column/direction are
// remembered in THIS VIEWER'S BROWSER (localStorage), never sent to or
// stored by the backend — per the owner's B-2 decision. Keyed by
// channel+template so a coach's many boards don't collide. Every access is
// try/catch-wrapped: a private window, cleared site data, or a blocked
// storage API must never break the leaderboard, only silently forget the
// preference (falls back to the server's own default).

type SortColumn = 'rank' | 'personal_best' | 'session_count' | 'last_eval_at' | 'display_name';
type SortOrder = 'asc' | 'desc';

interface LeaderboardViewerPrefs {
  metric?: LeaderboardRankedBy;
  sort?: SortColumn;
  order?: SortOrder;
}

function prefsStorageKey(slug: string, templateId?: string | null): string {
  return `sportform:leaderboard-prefs:${slug}:${templateId ?? 'all'}`;
}

function readLeaderboardPrefs(slug: string, templateId?: string | null): LeaderboardViewerPrefs {
  try {
    const raw = window.localStorage.getItem(prefsStorageKey(slug, templateId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LeaderboardViewerPrefs;
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeLeaderboardPrefs(
  slug: string,
  templateId: string | null | undefined,
  prefs: LeaderboardViewerPrefs,
): void {
  try {
    window.localStorage.setItem(prefsStorageKey(slug, templateId), JSON.stringify(prefs));
  } catch {
    // Private window / blocked storage / quota — the preference just won't
    // be remembered next time. Never throw.
  }
}

/** Reorders (never re-ranks — each entry's own `rank` is untouched)
 * `entries` for display, mirroring the backend's own `_apply_sort`
 * column set exactly. */
function applyClientSort(
  entries: LeaderboardEntry[],
  sort: SortColumn | undefined,
  order: SortOrder,
): LeaderboardEntry[] {
  if (!sort) return entries;
  const total = entries.length;
  const keyFn: Record<SortColumn, (e: LeaderboardEntry) => number | string> = {
    rank: (e) => e.rank ?? total + 1,
    personal_best: (e) => e.personal_best,
    session_count: (e) => e.session_count,
    last_eval_at: (e) => e.last_eval_at ?? '',
    display_name: (e) => (e.display_name ?? '').toLowerCase(),
  };
  const key = keyFn[sort];
  const sorted = [...entries].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (ka < kb) return -1;
    if (ka > kb) return 1;
    return 0;
  });
  return order === 'asc' ? sorted : sorted.reverse();
}

const SORT_COLUMNS: { key: SortColumn; label: string }[] = [
  { key: 'rank', label: 'Rank' },
  { key: 'personal_best', label: 'Score' },
  { key: 'session_count', label: 'Sessions' },
  { key: 'last_eval_at', label: 'Last active' },
  { key: 'display_name', label: 'Name' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(isoDate).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Medal colour/label for rank 1-3, plain ordinal for the rest. */
function rankDisplay(rank: number): { label: string; color: string } {
  if (rank === 1) return { label: '🥇', color: '#FFD700' };
  if (rank === 2) return { label: '🥈', color: '#C0C0C0' };
  if (rank === 3) return { label: '🥉', color: '#CD7F32' };
  return { label: `#${rank}`, color: 'var(--color-text-muted)' };
}

/**
 * Goal Score PR-6: on a small board (`board_size_mode === 'small_board'`),
 * `rank` is `null` and every entry carries a qualitative `badge` instead
 * ("Climbing" | "Steady" | "Building" — a tercile-based placeholder, see
 * `docs/API_BACKEND.md` §9.6, not the trend-based badge design docs evoke).
 * Never a number that functions as a disguised exact rank.
 */
function smallBoardBadgeStyle(badge: string): { bg: string; color: string } {
  if (badge === 'Climbing') return { bg: 'rgba(30,215,96,0.12)', color: 'var(--color-accent)' };
  if (badge === 'Building') return { bg: 'rgba(245,197,66,0.12)', color: '#F5C542' };
  return { bg: 'rgba(135,147,163,0.12)', color: 'var(--color-text-muted)' }; // Steady / unknown-future value
}

function scoreColor(score: number): string {
  if (score >= 85) return 'var(--color-accent)';
  if (score >= 70) return '#F5C542';
  return '#EF4444';
}

// ─── EntryBadges — fetches and renders top-3 badges for one leaderboard entry ─

interface EntryBadgesProps {
  userId: string;
}

function EntryBadges({ userId }: EntryBadgesProps) {
  const { data } = useUserMilestones(userId);
  if (!data || data.milestones.length === 0) return null;
  return <MiniMilestoneRow milestones={data.milestones} max={3} />;
}

// ─── LeaderboardRow ──────────────────────────────────────────────────────────

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  isMember: boolean;
  /** "Total" or "Form" — which metric `entry.personal_best` holds (Goal Score PR-6). */
  metricLabel: string;
}

function LeaderboardRow({ entry, isMember, metricLabel }: LeaderboardRowProps) {
  // Goal Score PR-6: `rank` is null on a small board — show the qualitative
  // badge instead of a medal/ordinal, never a number standing in for rank.
  const rankCell =
    entry.rank != null ? rankDisplay(entry.rank) : null;
  const isTopThree = entry.rank != null && entry.rank <= 3;
  // Goal Score follow-ups batch 1 (frontend): only non-null on a Total-ranked
  // board with a measured Goal bonus this session (J §1.3) — `null` on a
  // Form-ranked board or a legacy/unmeasured row, in which case the sub-line
  // is simply absent (never rendered as a broken "0 x 0 = 0" equation).
  // Gated on `metricLabel === 'Total'` in addition to the null-check, so a
  // Form-ranked board is byte-identical to pre-batch even in the
  // (contract-violating) case the backend sends populated fields on it.
  const breakdownLine =
    metricLabel === 'Total'
      ? leaderboardGoalBreakdownLine(
          entry.goal_bonus_score ?? null,
          entry.form_effect ?? null,
          entry.total_score ?? null,
        )
      : null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '12px 16px',
        borderRadius: '12px',
        backgroundColor: entry.is_current_user
          ? 'rgba(30,215,96,0.07)'
          : 'var(--color-dark-card)',
        border: entry.is_current_user
          ? '1px solid rgba(30,215,96,0.22)'
          : '1px solid var(--color-border-subtle)',
        transition: 'background-color 120ms ease',
        boxShadow: isTopThree ? 'var(--shadow-card)' : 'none',
      }}
    >
      {/* Rank (ranked board) OR qualitative badge (small board, PR-6) */}
      {rankCell ? (
        <div
          style={{
            minWidth: '36px',
            textAlign: 'center',
            fontSize: isTopThree ? '22px' : '14px',
            fontWeight: isTopThree ? 700 : 600,
            color: rankCell.color,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {rankCell.label}
        </div>
      ) : entry.badge ? (
        <div
          data-testid="leaderboard-small-board-badge"
          style={{
            minWidth: '36px',
            flexShrink: 0,
            textAlign: 'center',
            fontSize: '10px',
            fontWeight: 700,
            padding: '4px 6px',
            borderRadius: '999px',
            backgroundColor: smallBoardBadgeStyle(entry.badge).bg,
            color: smallBoardBadgeStyle(entry.badge).color,
            letterSpacing: '0.2px',
          }}
        >
          {entry.badge}
        </div>
      ) : (
        <div style={{ minWidth: '36px', flexShrink: 0 }} />
      )}

      {/* Name + you badge */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: '14px',
              fontWeight: entry.is_current_user ? 700 : 500,
              color: entry.is_current_user
                ? 'var(--color-accent)'
                : 'var(--color-text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {entry.display_name ?? 'Athlete'}
          </span>
          {entry.is_current_user && (
            <span
              style={{
                fontSize: '10px',
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: '999px',
                backgroundColor: 'rgba(30,215,96,0.15)',
                color: 'var(--color-accent)',
                border: '1px solid rgba(30,215,96,0.25)',
                letterSpacing: '0.3px',
                flexShrink: 0,
              }}
            >
              YOU
            </span>
          )}
        </div>
        <div
          style={{
            marginTop: '2px',
            fontSize: '11px',
            color: 'var(--color-text-muted)',
          }}
        >
          {entry.session_count} {entry.session_count === 1 ? 'session' : 'sessions'}
          {entry.last_eval_at && ` · ${formatRelativeTime(entry.last_eval_at)}`}
        </div>
        {/* Goal Score follow-ups batch 1 (frontend): "Goals 18 x Form effect 0.92 = Total 16.6" (J §1.3) */}
        {breakdownLine && (
          <div
            data-testid="leaderboard-goal-breakdown-line"
            style={{
              marginTop: '2px',
              fontSize: '11px',
              color: 'var(--color-text-muted)',
            }}
          >
            {breakdownLine}
          </div>
        )}
        {/* B-2026-0075: top-3 badge row (only for opted-in athletes visible to members) */}
        {isMember && <EntryBadges userId={String(entry.user_id)} />}
      </div>

      {/* PB Score */}
      <div
        style={{
          flexShrink: 0,
          textAlign: 'right',
        }}
      >
        <div
          style={{
            fontSize: isTopThree ? '20px' : '17px',
            fontWeight: 700,
            fontFamily: 'var(--font-heading)',
            color: scoreColor(entry.personal_best),
            letterSpacing: '-0.4px',
          }}
        >
          {entry.personal_best.toFixed(1)}
        </div>
        <div
          style={{
            fontSize: '10px',
            color: 'var(--color-text-muted)',
            fontWeight: 500,
            letterSpacing: '0.3px',
          }}
        >
          {/* Byte-identical to pre-PR-6 ("PB") when ranked by Form (the
              default, and the only state when the flag is off) — only a
              Total-ranked board gets the metric prefix, since "PB" alone
              would otherwise be ambiguous about which number is shown. */}
          {metricLabel === 'Total' ? 'Total PB' : 'PB'}
        </div>
      </div>
    </div>
  );
}

// ─── NonMemberTeaser ─────────────────────────────────────────────────────────

function NonMemberTeaser({ entryCount }: { entryCount: number }) {
  if (entryCount === 0) return null;
  return (
    <div
      style={{
        marginTop: '12px',
        padding: '12px 16px',
        borderRadius: '10px',
        backgroundColor: 'rgba(135,147,163,0.08)',
        border: '1px dashed var(--color-border-subtle)',
        textAlign: 'center',
        fontSize: '13px',
        color: 'var(--color-text-muted)',
      }}
    >
      Join this channel to see all {entryCount > 3 ? `${entryCount}+` : ''} ranked athletes.
    </div>
  );
}

// ─── ChannelLeaderboardTab ───────────────────────────────────────────────────

interface ChannelLeaderboardTabProps {
  slug: string;
}

export function ChannelLeaderboardTab({ slug }: ChannelLeaderboardTabProps) {
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // B2: `hasTotalColumn` is learned once from the server's own DEFAULT
  // (flag-driven) response and then never un-set for this mount — so the
  // toggle, once shown, stays visible even after the viewer switches to
  // "Form" (whose response reports `ranked_by: "form"` on its own).
  const [hasTotalColumn, setHasTotalColumn] = useState(false);
  const [metric, setMetric] = useState<LeaderboardRankedBy>('form');
  const [sortCol, setSortCol] = useState<SortColumn | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  // No template selector exists on this tab today (the board always
  // aggregates across all templates) — keyed on 'all' for forward-
  // compatibility with a future per-template filter.
  const templateIdForPrefs: string | null = null;

  // Goal Score go-live B5d (red-team finding F7): recomputed (not a
  // one-time snapshot) on every `slug`/`templateIdForPrefs` change, so a
  // parent that re-renders this tab with a DIFFERENT channel in place
  // (no remount) re-reads THAT channel's own remembered preferences
  // instead of carrying over the previous channel's.
  const storedPrefsRef = useMemo(
    () => readLeaderboardPrefs(slug, templateIdForPrefs),
    [slug, templateIdForPrefs]
  );

  useEffect(() => {
    // Re-apply (or reset to the no-preference default) every time the
    // board identity changes — never leaves a PRIOR channel's sort choice
    // visible on a newly-selected one.
    setSortCol(storedPrefsRef.sort);
    setSortOrder(storedPrefsRef.order ?? 'desc');
    // A different board may or may not have a Total column; `loadLeaderboard`
    // (triggered below via its own `storedPrefsRef` dependency) re-learns
    // this from the new channel's own default response.
    setHasTotalColumn(false);
  }, [storedPrefsRef]);

  const loadLeaderboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Always fetch the server's own DEFAULT first — this is what decides
      // `hasTotalColumn` and is also what a viewer who has never touched
      // the toggle sees (byte-identical to pre-B2).
      const natural = await getChannelLeaderboard(slug);
      const naturalRankedBy = natural.ranked_by ?? 'form';
      setHasTotalColumn(naturalRankedBy === 'total');

      const storedMetric = storedPrefsRef.metric;
      if (storedMetric && naturalRankedBy === 'total' && storedMetric !== naturalRankedBy) {
        // Remembered preference differs from the default — re-fetch under
        // the explicit override (only reachable when a Total column
        // genuinely exists; a toggle can never arm Total ranking).
        const overridden = await getChannelLeaderboard(slug, { metric: storedMetric });
        setData(overridden);
        setMetric(overridden.ranked_by ?? storedMetric);
      } else {
        setData(natural);
        setMetric(naturalRankedBy);
      }
    } catch {
      setError('Could not load the leaderboard. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [slug, storedPrefsRef]);

  useEffect(() => {
    void loadLeaderboard();
  }, [loadLeaderboard]);

  const handleMetricChange = useCallback(
    async (next: LeaderboardRankedBy) => {
      if (next === metric) return;
      setIsLoading(true);
      setError(null);
      try {
        const result = await getChannelLeaderboard(slug, { metric: next });
        setData(result);
        setMetric(result.ranked_by ?? next);
        writeLeaderboardPrefs(slug, templateIdForPrefs, {
          ...readLeaderboardPrefs(slug, templateIdForPrefs),
          metric: next,
        });
      } catch {
        setError('Could not load the leaderboard. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [slug, metric]
  );

  const handleSortChange = useCallback(
    (col: SortColumn) => {
      const nextOrder: SortOrder = col === sortCol && sortOrder === 'desc' ? 'asc' : 'desc';
      setSortCol(col);
      setSortOrder(nextOrder);
      writeLeaderboardPrefs(slug, templateIdForPrefs, {
        ...readLeaderboardPrefs(slug, templateIdForPrefs),
        sort: col,
        order: nextOrder,
      });
    },
    [slug, sortCol, sortOrder]
  );

  // B2: reorder (never re-rank) the already-fetched entries for display —
  // client-side, so clicking a column header never costs a round trip.
  const sortedEntries = useMemo(
    () => (data ? applyClientSort(data.entries, sortCol, sortOrder) : []),
    [data, sortCol, sortOrder]
  );

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <Spinner size="lg" label="Loading leaderboard…" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '32px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
        <p>{error}</p>
        <button
          type="button"
          onClick={() => void loadLeaderboard()}
          style={{
            marginTop: '12px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-accent)',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Leaderboard disabled
  if (data && !data.leaderboard_enabled) {
    return (
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px dashed var(--color-border-strong)',
          borderRadius: '20px',
          padding: '56px 32px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            backgroundColor: 'var(--color-dark-elevated-2)',
            border: '1px solid var(--color-border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <TrophyIcon style={{ width: '28px', height: '28px', color: 'var(--color-text-muted)' }} />
        </div>
        <h3
          style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            fontSize: '16px',
            color: 'var(--color-text-primary)',
            margin: '0 0 8px',
          }}
        >
          Leaderboard is disabled
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.6, maxWidth: '320px', margin: '0 auto' }}>
          The coach has turned off the leaderboard for this channel.
        </p>
      </div>
    );
  }

  // No opted-in athletes
  if (!data || data.entries.length === 0) {
    return (
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px dashed var(--color-border-strong)',
          borderRadius: '20px',
          padding: '56px 32px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            backgroundColor: 'var(--color-dark-elevated-2)',
            border: '1px solid var(--color-border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <UserGroupIcon style={{ width: '28px', height: '28px', color: 'var(--color-text-muted)' }} />
        </div>
        <h3
          style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            fontSize: '16px',
            color: 'var(--color-text-primary)',
            margin: '0 0 8px',
          }}
        >
          No rankings yet
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.6, maxWidth: '360px', margin: '0 auto' }}>
          Athletes need to opt in to appear on the leaderboard. Enable "Show me on the leaderboard" in your channel settings.
        </p>
      </div>
    );
  }

  const { entries, total_opted_in, is_member } = data;
  // Goal Score PR-6: 'form' is both the pre-PR-6 default (flag off — field
  // absent on a fixture/mock defaults it here too) and the live fallback
  // state. 'total' only when the flag is on AND a board has an eligible
  // published Total somewhere.
  const rankedBy = data.ranked_by ?? 'form';
  const metricLabel = rankedBy === 'total' ? 'Total' : 'Form';

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            fontSize: '18px',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          Leaderboard
        </h2>
        <span
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            fontWeight: 500,
          }}
        >
          {is_member
            ? `${total_opted_in} athlete${total_opted_in !== 1 ? 's' : ''} on the board`
            : 'Top 3 preview'}
        </span>
      </div>

      {/* Template name sub-header */}
      {data.template_name && (
        <p
          style={{
            fontSize: '13px',
            color: 'var(--color-text-muted)',
            marginBottom: '8px',
            marginTop: '-12px',
          }}
        >
          {/* Byte-identical to pre-PR-6 when ranked by Form (the default) —
              only a Total-ranked board gets the metric called out. */}
          {metricLabel === 'Total' ? 'Ranked by personal best Total score on: ' : 'Ranked by personal best on: '}
          <strong style={{ color: 'var(--color-text-secondary)' }}>{data.template_name}</strong>
        </p>
      )}

      {/* Goal Score PR-6: fallback notice — only set when ranked_by === 'form'
          because a Total ranking was requested/enabled but unavailable yet. */}
      {data.fallback_reason && (
        <p
          data-testid="leaderboard-fallback-reason"
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            marginBottom: '8px',
            lineHeight: 1.5,
          }}
        >
          {data.fallback_reason}
        </p>
      )}

      {/* Goal Score PR-6: board-scope disclosure. The backend sends it whenever
          template_id is set, flag or not; the tab shows it only on a Total-ranked
          board so the flag-OFF page stays byte-identical to today (PR-7a red-team
          finding 2). */}
      {data.ranked_by === 'total' && data.board_scope_disclosure && (
        <p
          data-testid="leaderboard-scope-disclosure"
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            marginBottom: '16px',
            lineHeight: 1.5,
            fontStyle: 'italic',
          }}
        >
          {data.board_scope_disclosure}
        </p>
      )}

      {/* B2 (Goal Score go-live): Total/Form toggle — only shown when this
          board actually has a Total column; a viewer who never touches it
          sees the server's own default, every time. */}
      {is_member && hasTotalColumn && (
        <div
          data-testid="leaderboard-metric-toggle"
          style={{ display: 'inline-flex', marginBottom: '14px', borderRadius: '8px', border: '1px solid var(--color-border-subtle)', overflow: 'hidden' }}
        >
          {(['total', 'form'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              data-testid={`leaderboard-metric-${opt}`}
              onClick={() => void handleMetricChange(opt)}
              aria-pressed={metric === opt}
              style={{
                fontSize: '12px',
                fontWeight: 600,
                padding: '6px 14px',
                border: 'none',
                cursor: metric === opt ? 'default' : 'pointer',
                backgroundColor: metric === opt ? 'var(--color-accent)' : 'transparent',
                color: metric === opt ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
              }}
            >
              {opt === 'total' ? 'Total' : 'Form'}
            </button>
          ))}
        </div>
      )}

      {/* B2 (Goal Score go-live): sortable columns — reorders the already-
          ranked rows for display; each row's own `rank` never changes. */}
      {is_member && entries.length > 1 && (
        <div
          data-testid="leaderboard-sort-headers"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}
        >
          {SORT_COLUMNS.map(({ key, label }) => {
            const isActive = sortCol === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`leaderboard-sort-${key}`}
                onClick={() => handleSortChange(key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: '999px',
                  border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border-subtle)'}`,
                  backgroundColor: isActive ? 'rgba(30,215,96,0.08)' : 'transparent',
                  color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
                  cursor: 'pointer',
                }}
              >
                {label}
                {isActive &&
                  (sortOrder === 'asc' ? (
                    <ChevronUpIcon style={{ width: '11px', height: '11px' }} />
                  ) : (
                    <ChevronDownIcon style={{ width: '11px', height: '11px' }} />
                  ))}
              </button>
            );
          })}
        </div>
      )}

      {/* Ranked entries */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {sortedEntries.map((entry) => (
          <LeaderboardRow key={entry.user_id} entry={entry} isMember={is_member} metricLabel={metricLabel} />
        ))}
      </div>

      {/* Non-member teaser */}
      {!is_member && <NonMemberTeaser entryCount={total_opted_in} />}

      {/* Footer note */}
      {is_member && (
        <p
          style={{
            marginTop: '16px',
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          Rankings are based on personal best scores. Only opted-in athletes appear here.
        </p>
      )}
    </div>
  );
}
